import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Paper,
  Avatar,
  Chip,
} from "@mui/material";
import {
  School,
  Science,
  Engineering,
  Computer,
  Psychology,
  Language,
  Business,
  Login as LoginIcon,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useAuth } from "../contexts/AuthContext";

// Academic Theme
const academicTheme = createTheme({
  palette: {
    primary: {
      main: "#1565C0", // สีน้ำเงินเข้ม
    },
    secondary: {
      main: "#FF8F00", // สีส้ม
    },
    background: {
      default: "#F8F9FA",
      paper: "#FFFFFF",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
    },
    h2: {
      fontWeight: 600,
      fontSize: "2rem",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.5rem",
    },
  },
});

const AcademicHomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ถ้าล็อคอินแล้วให้ไปหน้า dashboard ก่อนเสมอ
  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const faculties = [
    {
      name: "คณะวิศวกรรมศาสตร์",
      icon: <Engineering fontSize="large" />,
      color: "#1565C0",
      description: "วิศวกรรมคอมพิวเตอร์, วิศวกรรมไฟฟ้า, วิศวกรรมเครื่องกล",
    },
    {
      name: "คณะวิทยาศาสตร์",
      icon: <Science fontSize="large" />,
      color: "#388E3C",
      description: "คณิตศาสตร์, ฟิสิกส์, เคมี, ชีววิทยา",
    },
    {
      name: "คณะเทคโนโลยีสารสนเทศ",
      icon: <Computer fontSize="large" />,
      color: "#7B1FA2",
      description: "วิทยาการคอมพิวเตอร์, ระบบสารสนเทศ, ปัญญาประดิษฐ์",
    },
    {
      name: "คณะศิลปศาสตร์",
      icon: <Language fontSize="large" />,
      color: "#F57C00",
      description: "ภาษาอังกฤษ, ภาษาไทย, ปรัชญา, ศิลปกรรม",
    },
    {
      name: "คณะจิตวิทยา",
      icon: <Psychology fontSize="large" />,
      color: "#E91E63",
      description: "จิตวิทยาการศึกษา, จิตวิทยาคลินิก, จิตวิทยาอุตสาหกรรม",
    },
    {
      name: "คณะบริหารธุรกิจ",
      icon: <Business fontSize="large" />,
      color: "#795548",
      description: "การจัดการ, การตลาด, การเงิน, การบัญชี",
    },
  ];

  const news = [
    {
      title: "เปิดรับสมัครนักศึกษาใหม่ ปีการศึกษา 2568",
      date: "22 สิงหาคม 2568",
      type: "ประกาศ",
    },
    {
      title: "งานวิจัยด้าน AI ของมหาวิทยาลัยได้รับรางวัลระดับชาติ",
      date: "20 สิงหาคม 2568",
      type: "ข่าวสาร",
    },
    {
      title: "สัมมนาวิชาการ: อนาคตของเทคโนโลยีดิจิทัล",
      date: "18 สิงหาคม 2568",
      type: "กิจกรรม",
    },
  ];

  return (
    <ThemeProvider theme={academicTheme}>
      <Box sx={{ backgroundColor: "#F8F9FA", minHeight: "100vh" }}>
        {/* Header */}
        <AppBar
          position="static"
          sx={{ backgroundColor: "#FFFFFF", boxShadow: 1 }}
        >
          <Toolbar>
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <Avatar
                src="/academic-logo.png"
                alt="University Logo"
                sx={{
                  width: 50,
                  height: 50,
                  mr: 2,
                  backgroundColor: "#1565C0",
                }}
              >
                <School />
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ color: "#1565C0", fontWeight: 700 }}
                >
                  มหาวิทยาลัยเทคโนโลยีนวัตกรรม
                </Typography>
                <Typography variant="caption" sx={{ color: "#666" }}>
                  Innovation Technology University
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LoginIcon />}
              onClick={() => navigate("/login")}
              sx={{
                borderColor: "#1565C0",
                color: "#1565C0",
                "&:hover": {
                  backgroundColor: "#1565C0",
                  color: "white",
                },
              }}
            >
              เข้าสู่ระบบ
            </Button>
          </Toolbar>
        </AppBar>

        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)",
            color: "white",
            py: 8,
            textAlign: "center",
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="h1" gutterBottom>
              สร้างอนาคตด้วยนวัตกรรม
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
              มหาวิทยาลัยชั้นนำด้านเทคโนโลยีและวิทยาศาสตร์
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: "white",
                  color: "#1565C0",
                  "&:hover": { backgroundColor: "#F5F5F5" },
                }}
              >
                สมัครเรียน
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: "white",
                  color: "white",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                }}
              >
                เรียนรู้เพิ่มเติม
              </Button>
            </Box>
          </Container>
        </Box>

        {/* Features */}
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Typography
            variant="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: "#1565C0" }}
          >
            คณะวิชา
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            sx={{ mb: 6, color: "#666" }}
          >
            เลือกเส้นทางการศึกษาที่เหมาะกับคุณ
          </Typography>

          <Grid container spacing={4}>
            {faculties.map((faculty, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "transform 0.3s",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: "center" }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        backgroundColor: faculty.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                        color: "white",
                      }}
                    >
                      {faculty.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {faculty.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {faculty.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* News Section */}
        <Box sx={{ backgroundColor: "white", py: 8 }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              textAlign="center"
              gutterBottom
              sx={{ color: "#1565C0" }}
            >
              ข่าวสารและประกาศ
            </Typography>

            <Grid container spacing={3} sx={{ mt: 4 }}>
              {news.map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Paper sx={{ p: 3, height: "100%" }}>
                    <Chip
                      label={item.type}
                      size="small"
                      sx={{
                        mb: 2,
                        backgroundColor: "#E3F2FD",
                        color: "#1565C0",
                      }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.date}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Footer */}
      </Box>
    </ThemeProvider>
  );
};

export default AcademicHomePage;
